import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { User } from '../../model/user';
import { UserNotificationItem } from 'src/app/model/userNotificationItem';
import { UserNotificationService } from 'src/app/service/user.notification.service';
import { MatDialog } from '@angular/material/dialog';
import { NotificationsCenterComponent } from '../notifications-center/notifications-center.component';

// Sino de notificações — trigger que abre a Central de Notificações
// (`NotificationsCenterComponent`) num mat-dialog. Antes tinha dropdown
// próprio; agora reusa a MESMA UI da rota `/notifications` (com modo
// `in-dialog` ativado por injeção `@Optional() MatDialogRef`).
@Component({
  selector: 'app-user-notifications',
  templateUrl: './user-notifications.component.html',
  styleUrls: ['./user-notifications.component.scss']
})
export class UserNotificationsComponent implements OnInit {
  @Input() user: User;
  @Input() userIsAdmin: boolean;
  public notificationList: Array<UserNotificationItem>;
  public isThereNewNotifs = false;
  public unreadCount = 0;   // TOTAL de não lidas (do backend), não limitado ao size
  public isMenuOpen = false;   // reflete abertura do dialog p/ `menu-open` no bell

  constructor(
    private notificationService: UserNotificationService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private translate: TranslateService,
  ) {}

  /** aria-label do sino — inclui contagem de não lidas ("Notificações, 3"). */
  public get bellAriaLabel(): string {
    const base = this.translate.instant('menus.sidebar.notifications');
    if (this.unreadCount === 0) { return base; }
    return `${base}, ${this.unreadCount}`;
  }

  ngOnInit(): void {
    this.fetchUserNotifications();
    this.notificationService.messageStatusChangeTrigger$.subscribe(() => {
      this.fetchUserNotifications();
    });
  }

  /** Abre a Central de Notificações num dialog. O componente detecta que
   *  está em dialog via `@Optional() MatDialogRef` e adapta o layout
   *  (header in-flow, botão X, padding do body). */
  public openDialog(ev: Event): void {
    ev.stopPropagation();
    if (this.isMenuOpen) { return; } // guarda contra duplo clique
    this.isMenuOpen = true;
    const ref = this.dialog.open(NotificationsCenterComponent, {
      panelClass: 'notif-dialog-panel',
      // Backdrop = default do Material (`.cdk-overlay-dark-backdrop`,
      // ~0.288 opacidade). Mantém consistência com os ~160 outros
      // modais do app; sem `backdropClass` customizado.
      width: '720px',
      maxWidth: '92vw',
      maxHeight: '85vh',
      autoFocus: false,
      restoreFocus: true,
    });
    ref.afterClosed().subscribe(() => {
      this.isMenuOpen = false;
      // Após fechar, refetch pra sincronizar o badge (o usuário pode ter
      // lido/marcado várias notif no dialog).
      this.fetchUserNotifications();
    });
  }

  public fetchUserNotifications(): void {
    this.notificationService.refreshNotifications().subscribe(() => {
      this.updateData();
    });
    // NÃO pré-carregamos mais a lista COMPLETA aqui. Em usuários com muitas
    // notificações (medido: 1214 → ~500KB) o warm-up baixava esse payload em
    // TODA página (e 2× por causa do sino desktop + bottom-bar mobile), só pra o
    // dialog abrir instantâneo. O NotificationsCenterComponent já busca sozinho ao
    // abrir (getUserNotifications no ngOnInit, com spinner), então esse custo passa
    // a ser pago só quando o sino é ABERTO, não a cada navegação. O badge continua
    // via refreshNotifications() (byUser/ = só as recentes, leve).
    // `warmAllCache()` segue no service, disponível se quisermos reativar.
  }

  public updateData(): void {
    this.notificationList = this.notificationService.data;
    // Badge usa o total REAL de não lidas (não a length do array cappado).
    this.unreadCount = this.notificationService.unreadCount || 0;
    this.isThereNewNotifs = this.unreadCount > 0;
    this.cdr.detectChanges();
  }
}
